import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EmailMessage {
  id: string;
  sender: string;
  senderInitials: string;
  isOutgoing: boolean;
  timestamp: string;
  toLine: string;
  body: string;
}

export interface EmailThread {
  id: string;
  subject: string;
  preview: string;
  senderName: string;
  senderInitials: string;
  lastDate: string;
  replyCount: number;
  peopleCount: number;
  unreadCount: number;
  messages: EmailMessage[];
  linkedDoc?: { type: "invoice" | "quotation"; number: string };
}

// ─── Default seed data ────────────────────────────────────────────────────────

const SEED_THREADS: Record<string, EmailThread[]> = {
  "WO-2026-0001": [
    {
      id: "thread-seed-1",
      subject: "Initial Request: Parts Inquiry",
      preview:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam.",
      senderName: "Micheal Zeigler",
      senderInitials: "MZ",
      lastDate: "15 April 2026 - 9:30 AM",
      replyCount: 4,
      peopleCount: 2,
      unreadCount: 2,
      messages: [
        {
          id: "msg-seed-1-1",
          sender: "Micheal Zeigler",
          senderInitials: "MZ",
          isOutgoing: false,
          timestamp: "15 April 2026, 9:00 AM",
          toLine: "To: Me",
          body: "Dear James,\n\nI hope this email finds you well. I wanted to inquire about the parts needed for the rotary machine spinner repair on Work Order WO-2026-0001.\n\nCould you please confirm the availability of the oil filter kit and drive belt? We need to schedule the repair as soon as possible.\n\nBest regards,\nMicheal Zeigler",
        },
        {
          id: "msg-seed-1-2",
          sender: "James McDonald",
          senderInitials: "JM",
          isOutgoing: true,
          timestamp: "15 April 2026, 9:30 AM",
          toLine: "To: Micheal",
          body: "Hi Micheal,\n\nThank you for reaching out. I have checked the inventory and can confirm that both the oil filter kit and drive belt are available in stock.\n\nWe can schedule the repair for next week. I will send over the quotation shortly for your review.\n\nKind regards,\nJames McDonald",
        },
      ],
    },
    {
      id: "thread-seed-2",
      subject: "Service Agreement Renewal",
      preview:
        "Following up on our previous discussion regarding the service agreement renewal for your facility.",
      senderName: "Micheal Zeigler",
      senderInitials: "MZ",
      lastDate: "12 April 2026 - 2:15 PM",
      replyCount: 2,
      peopleCount: 2,
      unreadCount: 0,
      messages: [
        {
          id: "msg-seed-2-1",
          sender: "Micheal Zeigler",
          senderInitials: "MZ",
          isOutgoing: false,
          timestamp: "12 April 2026, 2:00 PM",
          toLine: "To: Me",
          body: "Dear James,\n\nI wanted to follow up on our previous discussion regarding the service agreement renewal for the Acme International facility.\n\nThe current agreement expires at the end of this quarter. Could we arrange a call to discuss the terms for renewal?\n\nBest regards,\nMicheal Zeigler",
        },
        {
          id: "msg-seed-2-2",
          sender: "James McDonald",
          senderInitials: "JM",
          isOutgoing: true,
          timestamp: "12 April 2026, 2:15 PM",
          toLine: "To: Micheal",
          body: "Hi Micheal,\n\nAbsolutely, I would be happy to discuss the service agreement renewal. Let us schedule a call for early next week.\n\nI will also prepare a summary of the services covered and the proposed terms for the new agreement.\n\nKind regards,\nJames McDonald",
        },
      ],
    },
  ],
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface WOEmailsContextValue {
  threads: Record<string, EmailThread[]>;
  addThread: (woNumber: string, thread: EmailThread) => void;
}

const WOEmailsContext = React.createContext<WOEmailsContextValue>({
  threads: {},
  addThread: () => undefined,
});

export function WOEmailsProvider({ children }: { children: React.ReactNode }) {
  const [threads, setThreads] = React.useState<Record<string, EmailThread[]>>(SEED_THREADS);

  function addThread(woNumber: string, thread: EmailThread) {
    setThreads((prev) => ({
      ...prev,
      [woNumber]: [thread, ...(prev[woNumber] ?? [])],
    }));
  }

  return (
    <WOEmailsContext.Provider value={{ threads, addThread }}>
      {children}
    </WOEmailsContext.Provider>
  );
}

export function useWOEmails(): WOEmailsContextValue {
  return React.useContext(WOEmailsContext);
}
